package meineglasfaser.frontend.admin.pages.incidents

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import io.ktor.client.call.*
import meineglasfaser.api.IncidentLevel
import meineglasfaser.api.IncidentStatus
import meineglasfaser.api.YaTableOptions
import meineglasfaser.api.routes.IncidentRoutes
import meineglasfaser.api.routes.InternalIncident
import meineglasfaser.api.routes.PrefetchRoutes
import meineglasfaser.frontend.admin.components.widgets.YaTable
import meineglasfaser.frontend.admin.components.widgets.named
import meineglasfaser.frontend.admin.components.widgets.sortedDesc
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.*
import meineglasfaser.frontend.common.components.yaform.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*

@Page
@Composable
fun IncidentListPage(ctx: PageContext) {
    var showNewIncidentDialog by remember { mutableStateOf(false) }
    var showViewIncidentDialog by remember { mutableStateOf<String?>(null) }
    var version by remember { mutableStateOf(0) }

    YaTable(
        header = { H1 { Text("Störungen") } },
        idProperty = InternalIncident::id,
        columns = listOf(
            InternalIncident::title named "Titel",
            InternalIncident::level named "Level",
            InternalIncident::status named "Status",
            InternalIncident::affectedNetworkId named "Betr. Netzwerk",
            InternalIncident::affectedComponentName named "Betr. Name",
            InternalIncident::createdAt named "Erstellt am",
            InternalIncident::createdBy named "Erstellt von",
        ),
        initialOptions = YaTableOptions.DEFAULT.copy(
            orderBy = listOf(
                InternalIncident::createdAt.sortedDesc(),
            ),
        ),
        fetch = { options ->
            BackendApi.post(IncidentRoutes.Internal()) {
                withAuth()
                setJsonBody(options)
            }
        },
        onNewEntry = {
            showNewIncidentDialog = true
        },
        onViewEntry = {
            showViewIncidentDialog = it.id
        },
        version = version,
    )

    if (showNewIncidentDialog) {
        NewIncidentDialog(
            onHide = {
                showNewIncidentDialog = false
                version++
            },
        )
    }
    showViewIncidentDialog?.let { incidentId ->
        ViewIncidentDialog(
            incidentId = incidentId,
            onHide = {
                showViewIncidentDialog = null
                version++
            },
        )
    }
}

@Composable
private fun NewIncidentDialog(onHide: () -> Unit) {
    val prefetchedNetworks = prefetch(PrefetchRoutes.Networks())

    Modal(onHide) {
        YaForm(
            onSubmitScope = { data ->
                val body = IncidentRoutes.Internal.New.RequestBody(
                    level = IncidentLevel.valueOf(data.string(IncidentRoutes.Internal.New.RequestBody::level)),
                    status = IncidentStatus.valueOf(data.string(IncidentRoutes.Internal.New.RequestBody::status)),
                    title = data.string(IncidentRoutes.Internal.New.RequestBody::title),
                    message = data.stringOrNull(IncidentRoutes.Internal.New.RequestBody::message) ?: "",
                    affectedNetworkId = data.stringOrNull(IncidentRoutes.Internal.New.RequestBody::affectedNetworkId),
                    affectedComponentName = data.stringOrNull(IncidentRoutes.Internal.New.RequestBody::affectedComponentName),
                    notifyCustomers = data.boolean(IncidentRoutes.Internal.New.RequestBody::notifyCustomers),
                    showAsBanner = data.boolean(IncidentRoutes.Internal.New.RequestBody::showAsBanner),
                )
                BackendApi.post(IncidentRoutes.Internal.New()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Erstellen der Störungsmeldung: ${error.message}"))
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Neue Störungsmeldung") }
            }
            YaFormDropdown(
                prop = IncidentRoutes.Internal.New.RequestBody::level,
                label = { Text("Level") },
                options = INCIDENT_LEVEL_STRINGS.entries.map { it.key to it.value },
                required = true,
            )
            YaFormDropdown(
                prop = IncidentRoutes.Internal.New.RequestBody::status,
                label = { Text("Status") },
                options = INCIDENT_STATUS_STRINGS.entries.map { it.key to it.value },
                required = true,
            )
            YaFormTextInput(
                prop = IncidentRoutes.Internal.New.RequestBody::title,
                label = { Text("Titel") },
                type = InputType.Text,
                required = true,
            )
            YaFormTextArea(
                prop = IncidentRoutes.Internal.New.RequestBody::message,
                label = { Text("Beschreibung") },
                rows = 4,
                required = false,
            )
            YaFormDropdown(
                prop = IncidentRoutes.Internal.New.RequestBody::affectedNetworkId,
                label = { Text("Betroffenes Netzwerk") },
                options = prefetchedNetworks,
            )
            YaFormTextInput(
                prop = IncidentRoutes.Internal.New.RequestBody::affectedComponentName,
                label = { Text("Betroffene Komponente") },
                type = InputType.Text,
                placeholder = "Kann genutzt werden, wenn Störung nicht ein Netz betrifft",
            )
            YaFormCheckInput(
                prop = IncidentRoutes.Internal.New.RequestBody::notifyCustomers,
                label = { Text("Kunden des Netzes über Störung benachrichtigen") },
            )
            YaFormCheckInput(
                prop = IncidentRoutes.Internal.New.RequestBody::showAsBanner,
                label = { Text("Störung als Banner im Shop anzeigen") },
            )
            ModalFooterButtons(onHide, submitLabel = "Erstellen")
        }
    }
}

@Composable
private fun ViewIncidentDialog(incidentId: String, onHide: () -> Unit) {
    var incident by remember {
        mutableStateOf<InternalIncident?>(null)
    }
    var showEditMetaDialog by remember { mutableStateOf(false) }
    var showAddEventDialog by remember { mutableStateOf(false) }
    var version by remember { mutableStateOf(0) }

    LaunchedEffect(incidentId, version) {
        BackendApi.get(IncidentRoutes.Internal.Get(incidentId)) {
            withAuth()
        }.fold(
            onSuccess = { response ->
                incident = response.body()
            },
            onFailure = { error ->
                Toasts.display(Toast.error("Fehler beim Laden der Störungsmeldung: ${error.message}"))
                onHide()
            }
        )
    }

    incident?.let { inc ->
        Modal(onHide, size = ModalSize.LG) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Störungsmeldung Details") }
            }
            // Metadata
            Ul({ classes("incident-meta") }) {
                Li { Text("ID: ${inc.id}") }
                Li { Text("Level: ${INCIDENT_LEVEL_STRINGS[inc.level] ?: inc.level.name}") }
                Li { Text("Status: ${INCIDENT_STATUS_STRINGS[inc.status] ?: inc.status.name}") }
                Li { Text("Titel: ${inc.title}") }
                Li { Text("Beschreibung: ${inc.message}") }
                Li { Text("Betroffenes Netzwerk: ${inc.affectedNetworkId ?: "-"}") }
                Li { Text("Betroffene Komponente: ${inc.affectedComponentName ?: "-"}") }
                Li { Text("Kunden benachrichtigen: ${if (inc.notifyCustomers) "Ja" else "Nein"}") }
                Li { Text("Als Banner anzeigen: ${if (inc.showAsBanner) "Ja" else "Nein"}") }
                Li { Text("Erstellt am: ${inc.createdAt}") }
                Li { Text("Erstellt von: ${inc.createdBy}") }
            }
            // Actions
            if (inc.status != IncidentStatus.FIXED) {
                Div({ classes("incident-actions") }) {
                    Button({ onClick { showEditMetaDialog = true } }) { Text("Metadaten bearbeiten") }
                    Button({ onClick { showAddEventDialog = true } }) { Text("Ereignis hinzufügen") }
                }
            }
            // Events
            H3 { Text("Ereignisse") }
            Div({ classes("incident-events") }) {
                if (inc.events.isEmpty()) {
                    Text("Keine Ereignisse vorhanden.")
                } else {
                    inc.events.forEach { event ->
                        Div({ classes("incident-event") }) {
                            Text("[${event.createdAt}] ${INCIDENT_LEVEL_STRINGS[event.level]} - ${INCIDENT_STATUS_STRINGS[event.status]}: ${event.message} (${event.createdBy})")
                        }
                    }
                }
            }
            // Dialogs
            if (showEditMetaDialog) {
                EditIncidentMetaDialog(incident = inc, onHide = {
                    showEditMetaDialog = false
                    version++
                })
            }
            if (showAddEventDialog) {
                AddIncidentEventDialog(incidentId = inc.id, inc.level, inc.status, onHide = {
                    showAddEventDialog = false
                    version++
                })
            }
        }
    }
}

@Composable
private fun EditIncidentMetaDialog(incident: InternalIncident, onHide: () -> Unit) {
    Modal(onHide) {
        YaForm(
            initial = IncidentRoutes.Internal.Update.RequestBody(
                title = incident.title,
                message = incident.message
            ),
            onSubmitScope = { data ->
                val body = IncidentRoutes.Internal.Update.RequestBody(
                    title = data.string(IncidentRoutes.Internal.Update.RequestBody::title),
                    message = data.stringOrNull(IncidentRoutes.Internal.Update.RequestBody::message) ?: "",
                )
                BackendApi.post(IncidentRoutes.Internal.Update(incident.id)) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Aktualisieren der Störungsmeldung: ${error.message}"))
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Metadaten bearbeiten") }
            }
            YaFormTextInput(
                prop = IncidentRoutes.Internal.Update.RequestBody::title,
                label = { Text("Titel") },
                type = InputType.Text,
                required = true,
            )
            YaFormTextArea(
                prop = IncidentRoutes.Internal.Update.RequestBody::message,
                label = { Text("Beschreibung") },
                rows = 4,
                required = false,
            )
            ModalFooterButtons(onHide, submitLabel = "Speichern")
        }
    }
}

@Composable
private fun AddIncidentEventDialog(incidentId: String, level: IncidentLevel, status: IncidentStatus, onHide: () -> Unit) {
    Modal(onHide) {
        YaForm(
            initial = IncidentRoutes.Internal.NewEvent.RequestBody(
                incidentId = incidentId,
                level = level,
                status = status,
                message = "",
            ),
            onSubmitScope = { data ->
                val body = IncidentRoutes.Internal.NewEvent.RequestBody(
                    incidentId = incidentId,
                    level = IncidentLevel.valueOf(data.string(IncidentRoutes.Internal.NewEvent.RequestBody::level)),
                    status = IncidentStatus.valueOf(data.string(IncidentRoutes.Internal.NewEvent.RequestBody::status)),
                    message = data.stringOrNull(IncidentRoutes.Internal.NewEvent.RequestBody::message) ?: "",
                )
                BackendApi.post(IncidentRoutes.Internal.NewEvent()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Hinzufügen des Ereignisses: ${error.message}"))
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Ereignis hinzufügen") }
            }
            YaFormDropdown(
                prop = IncidentRoutes.Internal.NewEvent.RequestBody::level,
                label = { Text("Neues Level") },
                options = INCIDENT_LEVEL_STRINGS.entries.map { it.key to it.value },
                required = true,
            )
            YaFormDropdown(
                prop = IncidentRoutes.Internal.NewEvent.RequestBody::status,
                label = { Text("Neuer Status") },
                options = INCIDENT_STATUS_STRINGS.entries.map { it.key to it.value },
                required = true,
            )
            YaFormTextArea(
                prop = IncidentRoutes.Internal.NewEvent.RequestBody::message,
                label = { Text("Beschreibung") },
                rows = 4,
                required = true,
            )
            ModalFooterButtons(onHide, submitLabel = "Hinzufügen")
        }
    }
}
